/**
 * Finance → Investors → one investor.
 *
 * Who they are, and each investment they hold — separately, with its own
 * terms, earnings, distributions, payments and what is still owed. Nothing on
 * this page adds one investment's money to another's, or one currency to
 * another.
 */

import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Plus } from 'lucide-react';
import {
  DEDUCTIBLE_EXPENSE_LABELS,
  FREQUENCY_LABELS,
  INVESTMENT_TYPE_EXPLANATIONS,
  LOSS_TREATMENT_LABELS,
  PAYMENT_METHOD_LABELS,
  PROFIT_INCOME_LABELS,
  VEHICLE_PAYOUT_BASIS_LABELS,
  type InvestmentDetail,
  type InvestmentView,
} from '@cenox/shared';
import { Button, Card, EmptyState, ErrorState, Spinner, cn } from '../../components/ui';
import { fetchBlob } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useModules } from '../../lib/modules';
import { money } from '../../lib/format';
import { InvestorFormDialog, PayoutForm } from '../riderFinance/InvestorForms';
import { LinkBikeDialog, ManageLinkedBikesDialog } from '../riderFinance/InvestorBikeDialogs';
import { useInvestor, useInvestorEarnings, useReviewInvestorEarning } from '../riderFinance/investorData';
import { InvestmentPanel } from './InvestmentPanel';
import { StatusBadge, TypeBadge } from './InvestmentsPage';
import {
  CorrectionDialog,
  InvestmentPaymentDialog,
  ProfitShareDialog,
  VehicleTermsDialog,
  VoidDialog,
} from './InvestmentDialogs';
import { useInvestment, useInvestments, useInvestorProfile, useVoidInvestmentMoney } from './investmentData';

function Term({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex gap-2 py-0.5">
      <dt className="w-40 shrink-0 text-slate-500">{label}</dt>
      <dd className="min-w-0 text-slate-900">{children}</dd>
    </div>
  );
}

function Figure({ label, value, currency, strong }: { label: string; value: string | null; currency: string; strong?: boolean }): ReactNode {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={cn('tabular-nums', strong ? 'text-base font-semibold text-violet-800' : 'text-sm font-medium text-slate-900')}>
        {value === null ? '—' : money(value, currency)}
      </p>
    </div>
  );
}

function Terms({ i }: { i: InvestmentDetail }): ReactNode {
  const cur = i.currencyCode;
  return (
    <dl className="text-sm">
      <Term label="Amount invested">{i.amountInvested !== null && cur ? money(i.amountInvested, cur) : 'Not recorded'}</Term>
      <Term label="Dates">
        {i.startsOn ?? 'Start not recorded'} {i.endsOn ? `to ${i.endsOn}` : '— open'}
      </Term>
      <Term label="Scope">{i.scope.label}</Term>
      {i.type === 'vehicle_partnership' ? (
        <>
          <Term label="Calculation basis">{i.vehiclePayoutBasis ? VEHICLE_PAYOUT_BASIS_LABELS[i.vehiclePayoutBasis] : 'Not agreed yet — each vehicle’s share applies'}</Term>
          {i.vehiclePayoutBasis === 'fixed_per_period' && i.vehicleFixedAmount ? <Term label="Per vehicle a week">{money(i.vehicleFixedAmount, i.money.currencyCode)}</Term> : null}
          {i.vehiclePayoutBasis === 'percent_of_remittance' ? <Term label="Percentage">{i.vehiclePercent}% of remittance</Term> : null}
        </>
      ) : null}
      {i.type === 'equity' ? (
        <>
          <Term label="Entity">{i.entity?.name ?? 'Not chosen'}</Term>
          <Term label="Ownership">{i.ownershipPercent ? `${i.ownershipPercent}% of ${i.ownershipBasis ?? 'the entity'}` : 'Not recorded'}</Term>
          <Term label="Payouts">Only through dividends declared — never automatic</Term>
        </>
      ) : null}
      {i.type === 'profit_sharing' ? (
        <>
          <Term label="Share of profit">{i.profitPercent ? `${i.profitPercent}%` : 'Not set'}</Term>
          <Term label="Calculated">{i.calculationPeriod ?? 'Not set'}</Term>
          <Term label="Eligible income">{i.eligibleIncome?.map((s) => PROFIT_INCOME_LABELS[s]).join(', ') || 'Not set'}</Term>
          <Term label="Expenses deducted">
            {i.deductibleExpenses ? DEDUCTIBLE_EXPENSE_LABELS[i.deductibleExpenses] : 'Not set'}
            {i.deductibleExpenses === 'selected' ? `: ${i.deductibleCategories.map((c) => c.name).join(', ')}` : ''}
          </Term>
          <Term label="Losses">{i.lossTreatment ? LOSS_TREATMENT_LABELS[i.lossTreatment] : 'Not set'}</Term>
          {i.operationalScope ? <Term label="Operational scope">{i.operationalScope}</Term> : null}
        </>
      ) : null}
      {i.payoutFrequency ? <Term label="Payout frequency">{FREQUENCY_LABELS[i.payoutFrequency]}</Term> : null}
      {i.reference ? <Term label="Reference">{i.reference}</Term> : null}
      {i.notes ? <Term label="Notes">{i.notes}</Term> : null}
    </dl>
  );
}

function InvestmentCard({ row, onMessage }: { row: InvestmentView; onMessage: (m: string) => void }): ReactNode {
  const { can, business } = useAuth();
  const path = useBusinessPath();
  const { active } = useModules();
  const detail = useInvestment(row.id);
  const vehicleInvestor = useInvestor(row.type === 'vehicle_partnership' ? row.investorId : null);
  const voider = useVoidInvestmentMoney();
  const mayManage = can('investments:manage');
  const mayPost = can('investments:post');
  const fleet = active.has('rider_finance');
  const [open, setOpen] = useState<null | 'edit' | 'pay' | 'profit' | 'correct' | 'terms' | 'link' | 'manage' | 'payout'>(null);
  const [voiding, setVoiding] = useState<{ kind: 'payment' | 'distribution'; id: string } | null>(null);
  const done = (m: string) => {
    setOpen(null);
    onMessage(m);
  };
  const i = detail.data;
  if (!i) return <Card>{detail.isError ? <ErrorState message="Could not load this investment." /> : <Spinner />}</Card>;
  const cur = i.money.currencyCode;
  const live = i.status === 'active' || i.status === 'ended';

  return (
    <section id={i.id} aria-label={`${i.type} investment`} className="card scroll-mt-20 space-y-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <TypeBadge type={i.type} />
            <StatusBadge status={i.status} />
            <span className="text-xs text-slate-500">{i.scope.label}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">{INVESTMENT_TYPE_EXPLANATIONS[i.type]}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {i.agreement ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<FileText className="size-4" />}
              onClick={async () => {
                const blob = await fetchBlob(path(`/investments/${i.id}/agreement`));
                window.open(URL.createObjectURL(blob), '_blank', 'noopener');
              }}
            >
              Agreement
            </Button>
          ) : null}
          {mayManage ? (
            <Button size="sm" variant="secondary" onClick={() => setOpen('edit')}>
              Edit
            </Button>
          ) : null}
        </div>
      </div>

      {i.missing.length ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">To complete: {i.missing.join(', ')}.</p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Terms i={i} />
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Money ({cur})</p>
          {i.money.vehicle ? (
            <>
              <div className="grid grid-cols-2 gap-2" aria-label="Partnership earnings">
                <Figure label="Total earnings due" value={i.money.vehicle.earningsDue} currency={cur} />
                <Figure label="Total collected" value={i.money.vehicle.collected} currency={cur} />
                <Figure label="Total paid out" value={i.money.vehicle.paidOut} currency={cur} />
                <Figure label="Outstanding balance" value={i.money.vehicle.outstandingBalance} currency={cur} strong />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Figure label="Collected, not yet paid out" value={i.money.vehicle.owed} currency={cur} />
                <Figure label="Still with riders" value={i.money.vehicle.unpaidByRiders} currency={cur} />
              </div>
              {i.money.vehicle.earningsInReviewCount > 0 ? (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {i.money.vehicle.earningsInReviewCount} week{i.money.vehicle.earningsInReviewCount === 1 ? '' : 's'} for review (
                  {money(i.money.vehicle.earningsInReview, cur)}): the agreement and records do not settle{' '}
                  {i.money.vehicle.earningsInReviewCount === 1 ? 'it' : 'them'}. Not counted as due until confirmed.
                </p>
              ) : null}
            </>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              <Figure label="Allocated" value={i.money.allocated} currency={cur} />
              <Figure label="Paid" value={i.money.paid} currency={cur} />
              <Figure label="Outstanding" value={i.money.outstanding} currency={cur} strong />
            </div>
          )}
          {i.money.vehicle ? (
            <p className="text-xs text-slate-500">
              Earnings are what the partnership agreement says is owed each week. Collected is the investor&apos;s share of what
              riders paid; only a recorded payout counts as paid out.
            </p>
          ) : null}
        </div>
      </div>

      {i.type === 'vehicle_partnership' ? (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">Linked vehicles</h3>
          {i.vehicles.length === 0 ? (
            <p className="text-sm text-slate-500">No vehicle linked yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm" aria-label="Linked vehicles">
              {i.vehicles.map((v) => (
                <li key={`${v.bikeId}:${v.from}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                  <span>
                    <span className="font-medium">{v.registration}</span>
                    <span className="text-xs text-slate-500"> · {v.category} · {v.riderName ?? 'no rider'}</span>
                  </span>
                  <span className="text-xs text-slate-500">
                    {v.share} · from {v.from}
                    {v.to ? ` to ${v.to}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            {fleet && mayManage && i.status === 'active' ? (
              <Button size="sm" variant="secondary" onClick={() => setOpen('link')}>Link vehicle</Button>
            ) : null}
            {fleet && mayManage && vehicleInvestor.data ? (
              <Button size="sm" variant="secondary" onClick={() => setOpen('manage')}>Manage linked vehicles</Button>
            ) : null}
            {fleet && mayManage && i.status !== 'draft' ? (
              <Button size="sm" variant="secondary" onClick={() => setOpen('terms')}>Change vehicle terms</Button>
            ) : null}
            {fleet && mayPost && i.status !== 'draft' && Number(i.money.vehicle?.owed ?? 0) > 0 ? (
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => setOpen('payout')}>Pay investor</Button>
            ) : null}
            {fleet ? (
              <Link to={`/finance/investors/${i.investorId}/weeks`} className="self-center text-sm font-medium text-violet-700 hover:underline">
                Weekly breakdown
              </Link>
            ) : null}
          </div>
          {open === 'payout' ? (
            <PayoutForm
              investorId={i.investorId}
              investmentId={i.id}
              payable={i.money.vehicle?.owed ?? '0'}
              currency={cur}
              onDone={() => setOpen(null)}
              onSaved={() => done('Payout recorded.')}
            />
          ) : null}
          <PartnershipEarnings investorId={i.investorId} investmentId={i.id} currency={cur} mayManage={Boolean(fleet && mayManage)} onDone={done} />
          {i.vehiclePayouts.length ? (
            <ul className="text-xs text-slate-500" aria-label="Vehicle payouts">
              {i.vehiclePayouts.map((p) => (
                <li key={p.id} className={cn(p.voided && 'line-through')}>
                  {p.paidOn} · {money(p.amount, cur)} · {PAYMENT_METHOD_LABELS[p.method as keyof typeof PAYMENT_METHOD_LABELS] ?? p.method}
                  {p.reference ? ` · ${p.reference}` : ''}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : (
        <section className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900">{i.type === 'equity' ? 'Dividends' : 'Profit shares'}</h3>
            {i.allocations.length === 0 ? (
              <p className="text-sm text-slate-500">
                {i.type === 'equity' ? 'No dividend declared to this holding yet.' : 'No period posted yet.'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm" aria-label="Distributions">
                {i.allocations.map((a) => (
                  <li key={a.id} className={cn('flex items-start justify-between gap-2 px-3 py-2', a.voided && 'opacity-50')}>
                    <span>
                      {a.label}
                      <span className="block text-xs text-slate-500">
                        {a.kind === 'adjustment' ? `Adjustment ${a.effectiveOn} — ${a.reason}` : a.effectiveOn}
                        {a.voided ? ' · voided' : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="tabular-nums">{money(a.amount, cur)}</span>
                      {mayPost && !a.voided && a.kind === 'allocation' ? (
                        <button type="button" className="text-xs text-slate-400 hover:text-red-600" onClick={() => setVoiding({ kind: 'distribution', id: a.distributionId })}>
                          Void
                        </button>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap gap-2">
              {i.type === 'profit_sharing' && mayPost && live ? (
                <Button size="sm" variant="secondary" onClick={() => setOpen('profit')}>Calculate profit share</Button>
              ) : null}
              {mayManage && mayPost && i.status !== 'draft' ? (
                <Button size="sm" variant="secondary" onClick={() => setOpen('correct')}>Correct dates and terms</Button>
              ) : null}
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900">Payments</h3>
            {i.payments.length === 0 ? (
              <p className="text-sm text-slate-500">Nothing paid yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm" aria-label="Payments">
                {i.payments.map((p) => (
                  <li key={p.id} className={cn('flex items-start justify-between gap-2 px-3 py-2', p.voided && 'opacity-50')}>
                    <span>
                      {p.paidOn} · {PAYMENT_METHOD_LABELS[p.method as keyof typeof PAYMENT_METHOD_LABELS] ?? p.method}
                      <span className="block text-xs text-slate-500">
                        {p.reference ?? 'No reference'}
                        {p.voided ? ` · voided: ${p.voidReason}` : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="tabular-nums">{money(p.amount, p.currencyCode)}</span>
                      {mayPost && !p.voided ? (
                        <button type="button" className="text-xs text-slate-400 hover:text-red-600" onClick={() => setVoiding({ kind: 'payment', id: p.id })}>
                          Void
                        </button>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {mayPost && live && Number(i.money.outstanding ?? 0) > 0 ? (
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => setOpen('pay')}>Record payment</Button>
            ) : null}
          </div>
        </section>
      )}

      {i.history.length ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-600">History ({i.history.length})</summary>
          <ul className="mt-2 space-y-1 text-xs text-slate-600">
            {i.history.map((h) => (
              <li key={`${h.at}-${h.summary}`}>
                {new Date(h.at).toLocaleString('en-GB', { timeZone: business?.timezone ?? 'UTC' })} · {h.by ?? 'CenoX'} — {h.summary}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {open === 'edit' ? <InvestmentPanel investment={i} onClose={() => setOpen(null)} onSaved={(m) => done(m)} /> : null}
      {open === 'pay' ? <InvestmentPaymentDialog investment={i} onClose={() => setOpen(null)} onDone={done} /> : null}
      {open === 'profit' ? <ProfitShareDialog investment={i} onClose={() => setOpen(null)} onDone={done} /> : null}
      {open === 'correct' ? <CorrectionDialog investment={i} onClose={() => setOpen(null)} onDone={done} /> : null}
      {open === 'terms' ? <VehicleTermsDialog investment={i} onClose={() => setOpen(null)} onDone={done} /> : null}
      {open === 'link' ? <LinkBikeDialog investorId={i.investorId} investmentId={i.id} onClose={() => setOpen(null)} onDone={done} /> : null}
      {open === 'manage' && vehicleInvestor.data ? (
        <ManageLinkedBikesDialog investor={vehicleInvestor.data} currency={cur} onClose={() => setOpen(null)} onLink={() => setOpen('link')} />
      ) : null}
      {voiding ? (
        <VoidDialog
          title={voiding.kind === 'payment' ? 'Void this payment' : 'Void this distribution'}
          pending={voider.isPending}
          error={voider.error}
          onClose={() => setVoiding(null)}
          onConfirm={(reason) =>
            voider.mutate({ ...voiding, reason }, { onSuccess: () => { setVoiding(null); onMessage('Voided. The record stays, marked void.'); } })
          }
        />
      ) : null}
    </section>
  );
}

export function InvestorProfilePage(): ReactNode {
  const { investorId } = useParams<{ investorId: string }>();
  const { can } = useAuth();
  const profile = useInvestorProfile(investorId ?? null);
  const investments = useInvestments(investorId ? { investorId } : {});
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (!can('investments:read')) {
    return <EmptyState title="Investors are for the owner and those the owner allows" description="Ask the owner for access to investments." />;
  }
  if (profile.isError) return <ErrorState message="Could not load this investor." onRetry={() => void profile.refetch()} />;
  if (!profile.data) return <Spinner />;
  const p = profile.data;
  const missing = [!p.phone && 'phone', !p.email && 'email', !p.address && 'address'].filter(Boolean) as string[];
  const rows = investments.data?.rows ?? [];

  return (
    <div className="space-y-5">
      <Link to="/finance/investors" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-violet-700">
        <ArrowLeft className="size-4" /> Investors
      </Link>
      <Card className="flex flex-wrap items-start justify-between gap-4 border-violet-100">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-slate-900">{p.name}</h1>
          <p className="text-sm text-slate-500">
            {p.reference ?? 'No investor ID'}
            {p.isActive ? '' : ' · inactive'}
          </p>
          <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
            <div><dt className="text-xs text-slate-500">Phone</dt><dd>{p.phone ?? '—'}</dd></div>
            <div><dt className="text-xs text-slate-500">Email</dt><dd className="break-all">{p.email ?? '—'}</dd></div>
            <div><dt className="text-xs text-slate-500">Address</dt><dd>{p.address ?? '—'}</dd></div>
          </dl>
          {missing.length ? <p className="mt-2 text-xs text-amber-700">Not recorded yet: {missing.join(', ')}.</p> : null}
        </div>
        {can('investments:manage') ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Edit profile</Button>
            <Button size="sm" icon={<Plus className="size-4" />} className="bg-violet-600 hover:bg-violet-700" onClick={() => setAdding(true)}>
              Add investment
            </Button>
          </div>
        ) : null}
      </Card>

      {message ? (
        <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">{message}</p>
      ) : null}

      <section className="space-y-4" aria-label="Investments">
        <h2 className="text-base font-semibold text-slate-900">Investments ({rows.length})</h2>
        {investments.isPending ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState title="No investment yet" description="Add the vehicle partnership, equity or profit share this investor holds." />
        ) : (
          rows.map((r) => <InvestmentCard key={r.id} row={r} onMessage={setMessage} />)
        )}
      </section>

      {adding ? (
        <InvestmentPanel investorId={p.id} onClose={() => setAdding(false)} onSaved={(m) => { setAdding(false); setMessage(m); }} />
      ) : null}
      {editing ? (
        <InvestorFormDialog investor={p} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); setMessage('Investor details saved.'); }} />
      ) : null}
    </div>
  );
}

const EARNING_TONES: Record<'accrued' | 'review' | 'reversed', string> = {
  accrued: 'bg-emerald-50 text-emerald-800',
  review: 'bg-amber-50 text-amber-900',
  reversed: 'bg-slate-100 text-slate-500 line-through',
};

/** The partnership's weekly earnings (0171), with the owner's review. */
function PartnershipEarnings({
  investorId,
  investmentId,
  currency,
  mayManage,
  onDone,
}: {
  investorId: string;
  investmentId: string;
  currency: string;
  mayManage: boolean;
  onDone: (message: string) => void;
}): ReactNode {
  const list = useInvestorEarnings(investorId);
  const review = useReviewInvestorEarning();
  const [acting, setActing] = useState<{ id: string; action: 'confirm' | 'reverse' } | null>(null);
  const [text, setText] = useState('');
  const [value, setValue] = useState('');
  const rows = (list.data ?? []).filter((r) => r.investmentId === investmentId);
  if (list.isLoading) return <Spinner />;
  if (list.isError) return <ErrorState message="Could not load the weekly earnings." onRetry={() => void list.refetch()} />;
  return (
    <section className="space-y-2" aria-label="Weekly earnings">
      <h3 className="text-sm font-semibold text-slate-900">Weekly earnings</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No week has ended under this partnership yet, or its terms do not yet state a start date and amount.</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
          {rows.map((r) => (
            <li key={r.id} className="space-y-1 px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  <span className="font-medium">{r.weekStartsOn} – {r.weekEndsOn}</span>
                  <span className="text-xs text-slate-500"> · {r.registration}</span>
                </span>
                <span className="flex items-center gap-2 tabular-nums">
                  <span className={cn('rounded px-1.5 py-0.5 text-xs', EARNING_TONES[r.status])}>
                    {r.status === 'accrued' ? 'Due' : r.status === 'review' ? 'For review' : 'Reversed'}
                  </span>
                  <span>{money(r.amount, currency)}</span>
                  <span className="text-xs text-slate-500">collected {money(r.collected, currency)}</span>
                </span>
              </div>
              {r.reviewReason ? <p className="text-xs text-amber-900">{r.reviewReason}</p> : null}
              {r.coveredDespiteOwnership ? (
                <p className="text-xs text-slate-500">Earned under the active partnership; the ownership record for the week shows the company.</p>
              ) : null}
              {r.reversalReason ? <p className="text-xs text-slate-500">Reversed: {r.reversalReason}</p> : null}
              {mayManage && r.status !== 'reversed' ? (
                acting?.id === r.id ? (
                  <form
                    className="flex flex-wrap items-end gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      review.mutate(
                        acting.action === 'confirm'
                          ? { id: r.id, action: 'confirm', note: text, ...(value ? { amount: value } : {}) }
                          : { id: r.id, action: 'reverse', reason: text },
                        {
                          onSuccess: () => {
                            setActing(null);
                            setText('');
                            setValue('');
                            onDone(acting.action === 'confirm' ? 'Earning confirmed.' : 'Earning reversed.');
                          },
                        },
                      );
                    }}
                  >
                    {acting.action === 'confirm' ? (
                      <label className="text-xs text-slate-600">
                        Amount
                        <input className="ml-1 w-28 rounded border border-slate-300 px-2 py-1" inputMode="decimal" placeholder={r.amount} value={value} onChange={(e) => setValue(e.target.value)} />
                      </label>
                    ) : null}
                    <label className="flex-1 text-xs text-slate-600">
                      {acting.action === 'confirm' ? 'Why it is owed' : 'Why it is not owed'}
                      <input className="ml-1 w-full rounded border border-slate-300 px-2 py-1" required value={text} onChange={(e) => setText(e.target.value)} />
                    </label>
                    <Button type="submit" size="sm" disabled={review.isPending || !text.trim()}>
                      {acting.action === 'confirm' ? 'Confirm' : 'Reverse'}
                    </Button>
                    <Button type="button" size="sm" variant="secondary" onClick={() => setActing(null)}>Cancel</Button>
                    {review.error ? <p className="w-full text-xs text-red-700">{review.error.message}</p> : null}
                  </form>
                ) : (
                  <div className="flex gap-2">
                    {r.status === 'review' ? (
                      <Button size="sm" variant="secondary" onClick={() => setActing({ id: r.id, action: 'confirm' })}>Confirm</Button>
                    ) : null}
                    <Button size="sm" variant="secondary" onClick={() => setActing({ id: r.id, action: 'reverse' })}>Reverse</Button>
                  </div>
                )
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
